import { useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import {
  mockApi,
  TYPE_LABEL_KEY,
  type MockReviewBlock,
  type MockReviewEvidence,
  type MockReviewItem,
  type MockReviewSection,
} from '@/api/ieltsMock';
import { AudioPlayer } from '@/components/ui/AudioPlayer';
import { Card, CardTitle } from '@/components/ui/Card';
import { Pill } from '@/components/ui/Pill';
import { clock, playFrom, questionNumber, readableAnswer, splitHighlight } from '@/lib/mockReview';
import { cn } from '@/lib/utils';

/**
 * MockReviewPanel — разбор сданного мока (20.10.2026, решение владельца: без
 * разбора мок на прод не идёт). По каждому вопросу: «Ваш ответ / Правильный
 * ответ / Почему» и опора — абзац текста с выделением и буквой или реплика
 * записи с выделением и временем («Слушать с …»).
 *
 * Объяснение — одно, на английском, как сам экзамен. С 20.10.2026 и подписи
 * по-английски: панель живёт только внутри мока, а мок целиком английский
 * (`lib/mockEnglish.ts`). Почему ВЫБРАННЫЙ ответ неверен — сознательно не объясняем.
 */
export function MockReviewPanel() {
  const { t } = useTranslation();
  const [onlyWrong, setOnlyWrong] = useState(false);
  const q = useQuery({ queryKey: ['ielts-mock-review'], queryFn: mockApi.review, staleTime: 60_000 });

  if (q.isLoading) {
    return (
      <Card variant="subtle">
        <p className="text-sm text-on-surface-variant">{t('mockReview.loading')}</p>
      </Card>
    );
  }
  // ⚠️ ПУСТОЙ СПИСОК С СЕРВЕРА МОЖЕТ ПРИЙТИ NULL (Go), а не []: нормализуем здесь.
  const sections = (q.data?.sections ?? []).filter(Boolean);
  if (q.isError || sections.length === 0) {
    return (
      <Card variant="subtle">
        <p className="text-sm text-on-surface-variant">{t('mockReview.unavailable')}</p>
      </Card>
    );
  }

  return (
    <div className="space-y-6" data-testid="mock-review">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-display text-xl font-bold text-on-surface">{t('mockReview.title')}</h2>
        <button
          onClick={() => setOnlyWrong((v) => !v)}
          className="rounded-pill border border-outline px-3 py-1 text-sm font-medium hover:border-primary"
        >
          {t(onlyWrong ? 'mockReview.showAll' : 'mockReview.onlyWrong')}
        </button>
      </div>
      {sections.some((s) => s.band != null) && (
        <p className="text-xs text-on-surface-variant">{t('ieltsMock.approxBandNote')}</p>
      )}
      {sections.map((s) => (
        <ReviewSection key={s.skill} section={s} onlyWrong={onlyWrong} />
      ))}
    </div>
  );
}

function ReviewSection({ section, onlyWrong }: { section: MockReviewSection; onlyWrong: boolean }) {
  const { t } = useTranslation();
  return (
    <section className="space-y-4">
      <div className="flex items-baseline justify-between gap-3 border-b border-outline pb-2">
        <CardTitle>{t(`trainersHub.skill.${section.skill}`)}</CardTitle>
        <span className="tabular-nums text-sm text-on-surface-variant">
          {t('mockReview.sectionScore', { got: section.points, max: section.max_points })}
          {section.band != null && ` · ${t('ieltsMock.approxBand', { band: section.band.toFixed(1) })}`}
        </span>
      </div>
      {(section.blocks ?? []).map((b) => (
        <ReviewBlock key={b.source_id} block={b} skill={section.skill} onlyWrong={onlyWrong} />
      ))}
    </section>
  );
}

function ReviewBlock({ block, skill, onlyWrong }: { block: MockReviewBlock; skill: string; onlyWrong: boolean }) {
  const { t } = useTranslation();
  // «Слушать с …» — переход общего плеера к месту ответа (nonce — каждое нажатие).
  const [seek, setSeek] = useState<{ sec: number; nonce: number } | null>(null);
  const nonce = useRef(0);
  const listen = (sec: number) => {
    nonce.current += 1;
    setSeek({ sec: playFrom(sec), nonce: nonce.current });
  };
  const groups = (block.groups ?? []).map((g) => ({
    ...g,
    items: (g.items ?? []).filter((it) => !onlyWrong || !it.correct),
  }));
  if (groups.every((g) => g.items.length === 0)) return null;

  const heading =
    skill === 'listening' && block.part
      ? t('mockReview.part', { n: block.part })
      : skill === 'reading' && block.passage
        ? t('mockReview.passage', { n: block.passage })
        : null;

  return (
    <Card className="space-y-4">
      <div className="space-y-1">
        {heading && <div className="text-xs font-semibold uppercase tracking-wide text-primary">{heading}</div>}
        <div className="font-semibold">{block.title}</div>
        {skill === 'listening' && block.audio_url && (
          // Своя запись в разборе: «Слушать с …» перематывает её к месту ответа.
          <AudioPlayer src={block.audio_url} preload="none" knownSeconds={block.duration_sec} jumpTo={seek} className="mt-2" />
        )}
      </div>
      {groups.map(
        (g, gi) =>
          g.items.length > 0 && (
            <div key={gi} className="space-y-3">
              <div className="flex flex-wrap items-center gap-2">
                <Pill variant="primary">{t(TYPE_LABEL_KEY[g.type] ?? g.type)}</Pill>
                {g.group_label && <span className="text-xs text-on-surface-variant">{g.group_label}</span>}
              </div>
              <ol className="space-y-3">
                {g.items.map((it) => (
                  <ReviewItemCard key={it.question_id} item={it} skill={skill} onListen={listen} />
                ))}
              </ol>
            </div>
          ),
      )}
    </Card>
  );
}

function ReviewItemCard({
  item,
  skill,
  onListen,
}: {
  item: MockReviewItem;
  skill: string;
  onListen: (sec: number) => void;
}) {
  const { t } = useTranslation();
  const partial = !item.correct && item.points > 0;
  const verdict = item.correct ? 'success' : partial ? 'warning' : 'danger';
  const yours = readableAnswer(item.type, item.student_answer);
  return (
    <li
      className={cn(
        'space-y-2 rounded-2xl border p-4',
        item.correct
          ? 'border-success/40 bg-success-container/20'
          : partial
            ? 'border-warning/40 bg-warning-container/20'
            : 'border-error/40 bg-error-container/20',
      )}
    >
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-semibold tabular-nums">
          {t('mockReview.question', { n: questionNumber(item.ord, item.ord_to) })}
        </span>
        <Pill variant={verdict}>
          {item.correct
            ? t('mockReview.correct')
            : partial
              ? t('mockReview.partial', { got: item.points, max: item.max_points })
              : t('mockReview.wrong')}
        </Pill>
      </div>
      <p className="text-[15px]">{item.prompt}</p>
      <dl className="grid gap-1 text-sm sm:grid-cols-[auto_1fr] sm:gap-x-3">
        <dt className="text-on-surface-variant">{t('mockReview.yourAnswer')}</dt>
        <dd className="font-semibold">{yours || t('mockReview.noAnswer')}</dd>
        <dt className="text-on-surface-variant">{t('mockReview.correctAnswer')}</dt>
        <dd className="font-semibold">{readableAnswer(item.type, item.correct_answer)}</dd>
      </dl>
      <div className="text-sm">
        <span className="text-on-surface-variant">{t('mockReview.why')} </span>
        {item.explanation ? (
          <span lang="en">{item.explanation}</span>
        ) : (
          <span className="text-on-surface-variant">{t('mockReview.explanationPending')}</span>
        )}
      </div>
      {item.evidence && <EvidenceView ev={item.evidence} skill={skill} onListen={onListen} />}
    </li>
  );
}

function EvidenceView({
  ev,
  skill,
  onListen,
}: {
  ev: MockReviewEvidence;
  skill: string;
  onListen: (sec: number) => void;
}) {
  const { t } = useTranslation();
  const [before, mark, after] = useMemo(() => splitHighlight(ev.context, ev.hl_start, ev.hl_end), [ev]);
  const where =
    skill === 'reading'
      ? ev.paragraph
        ? t('mockReview.paragraph', { p: ev.paragraph })
        : ev.paragraph_index
          ? t('mockReview.paragraph', { p: ev.paragraph_index })
          : null
      : ev.speaker ?? null;
  const time = ev.time_sec != null ? (ev.time_approx ? `≈ ${clock(ev.time_sec)}` : clock(ev.time_sec)) : null;
  return (
    <figure className="space-y-2 rounded-xl border border-outline bg-surface/70 p-3 text-sm">
      <figcaption className="flex flex-wrap items-center gap-2 text-xs text-on-surface-variant">
        <span>{t(skill === 'reading' ? 'mockReview.whereText' : 'mockReview.whereAudio')}</span>
        {where && <Pill>{where}</Pill>}
        {time && <span className="tabular-nums">{time}</span>}
        {time && ev.time_sec != null && (
          <button
            onClick={() => onListen(ev.time_sec ?? 0)}
            className="rounded-pill border border-outline px-2 py-0.5 font-medium text-on-surface hover:border-primary"
          >
            {t('mockReview.listenFrom', { time: clock(playFrom(ev.time_sec)) })}
          </button>
        )}
      </figcaption>
      <blockquote lang="en" className="leading-relaxed">
        {before}
        <mark className="rounded bg-primary-fixed px-0.5 text-on-surface">{mark}</mark>
        {after}
      </blockquote>
    </figure>
  );
}
